import React, { useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  IconButton,
  Divider,
  Button,
  useTheme,
  alpha,
} from "@mui/material";
import {
  BatteryAlert,
  Battery20,
  ChevronRight,
  Warning,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useLowBatteryDevices } from "../../hooks/useLowBatteryDevices";
import { useTranslation } from "react-i18next";
import { logger } from "../../utils/logger";

const LowBatteryPanel = memo(({ devices }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { t } = useTranslation("common");

  const {
    lowBatteryDevices,
    criticalBatteryDevices,
    totalLowBattery,
    totalCriticalBattery,
  } = useLowBatteryDevices(devices);

  const handleDeviceClick = useCallback(
    (deviceId) => {
      navigate(`/device/${deviceId}`);
    },
    [navigate]
  );

  const handleViewAllClick = useCallback(() => {
    // Navigate to devices page with battery filter
    // Test missing key - this should trigger the debugger
    logger.warn("Testing missing key:", t("test.missing_key_for_debugging"));
    navigate("/device?battery=low");
  }, [navigate, t]);

  // Combine and limit devices shown (show critical first, then low)
  const devicesToShow = [
    ...criticalBatteryDevices.slice(0, 3),
    ...lowBatteryDevices.slice(
      0,
      Math.max(0, 5 - criticalBatteryDevices.length)
    ),
  ];

  const totalDevicesWithLowBattery = totalLowBattery + totalCriticalBattery;

  if (totalDevicesWithLowBattery === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
              : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)"
          }`,
        }}
      >
        <Box display="flex" alignItems="center" mb={2}>
          <Battery20
            sx={{
              color: theme.palette.success.main,
              fontSize: "1.5rem",
              mr: 1,
            }}
          />
          <Typography variant="h6" fontWeight={600}>
            {t("common.battery_status")}
          </Typography>
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={4}
        >
          <Typography variant="h4" color="success.main" fontWeight={600} mb={1}>
            ✓
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("common.all_batteries_good")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
            : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
        backdropFilter: "blur(10px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.1)"
        }`,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <BatteryAlert
            sx={{
              color:
                totalCriticalBattery > 0
                  ? theme.palette.error.main
                  : theme.palette.warning.main,
              fontSize: "1.5rem",
              mr: 1,
            }}
          />
          <Typography variant="h6" fontWeight={600}>
            {t("common.low_battery_devices")}
          </Typography>
        </Box>

        {/* Summary chips */}
        <Box display="flex" gap={1}>
          {totalCriticalBattery > 0 && (
            <Chip
              size="small"
              icon={<ErrorIcon />}
              label={`${totalCriticalBattery} Critical`}
              color="error"
              variant="filled"
            />
          )}
          {totalLowBattery > 0 && (
            <Chip
              size="small"
              icon={<Warning />}
              label={`${totalLowBattery} Low`}
              color="warning"
              variant="filled"
            />
          )}
        </Box>
      </Box>

      {/* Device List */}
      <List sx={{ py: 0 }}>
        {devicesToShow.map((device, index) => {
          const isCritical = device.batteryPercent <= 10;
          const batteryColor = isCritical
            ? theme.palette.error.main
            : theme.palette.warning.main;

          return (
            <React.Fragment key={device.id}>
              <ListItem
                sx={{
                  px: 0,
                  py: 1.5,
                  cursor: "pointer",
                  borderRadius: 2,
                  transition: "background-color 0.2s",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  },
                }}
                onClick={() => handleDeviceClick(device.id)}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    bgcolor: alpha(batteryColor, 0.1),
                    color: batteryColor,
                  }}
                >
                  {isCritical ? <ErrorIcon /> : <Warning />}
                </Avatar>

                <ListItemText
                  primary={
                    <Typography variant="subtitle2" fontWeight={500}>
                      {device.name || `Device ${device.id}`}
                    </Typography>
                  }
                  secondary={
                    <Box
                      component="span"
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mt={0.5}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Battery: {device.batteryPercent}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {device.batteryVoltage}mV
                      </Typography>
                    </Box>
                  }
                />

                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeviceClick(device.id);
                    }}
                  >
                    <ChevronRight />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>

              {index < devicesToShow.length - 1 && (
                <Divider variant="inset" component="li" />
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* View All Button */}
      {totalDevicesWithLowBattery > 5 && (
        <Box mt={2} textAlign="center">
          <Button
            variant="text"
            color="primary"
            onClick={handleViewAllClick}
            size="small"
          >
            View All {totalDevicesWithLowBattery} Devices
          </Button>
        </Box>
      )}
    </Paper>
  );
});

LowBatteryPanel.displayName = "LowBatteryPanel";

export default LowBatteryPanel;
